// PlayerCard Component
// Card component for displaying player information in search results

import React from 'react';
import { FaStar, FaMapMarkerAlt, FaBookmark, FaRegBookmark, FaTrophy, FaRunning } from 'react-icons/fa';
import styles from './PlayerCard.module.css';
import { calculateAge } from '../../../utils/playerProfile/profileHelpers';

const PlayerCard = ({ profile, isInWatchlist, onToggleWatchlist, onClick, showWatchlistButton = true }) => {
  // Calculate derived data
  const age = profile.dateOfBirth ? calculateAge(profile.dateOfBirth) : null;
  const averageRating = profile.averageSkillRating || 0;
  const location = profile.location 
    ? [profile.location.city, profile.location.state, profile.location.country]
        .filter(Boolean)
        .join(', ')
    : '';
  
  // Get profile name from various possible locations
  const profileName = profile.name || 
                     (profile.userId ? `${profile.userId.firstName} ${profile.userId.lastName}` : '') ||
                     'Player';
  
  // Get profile photo from various possible locations
  const profilePhoto = profile.profilePhoto || 
                      profile.media?.profileImage?.cloudinary?.url ||
                      profile.media?.profileImage?.url ||
                      null;

  // Get primary sport icon
  const getSportIcon = () => {
    // You can expand this with sport-specific icons
    return <FaRunning />;
  };

  return (
    <div 
      className={styles.card} 
      onClick={onClick}
      style={{ cursor: 'pointer' }}
      title="Click to view player profile in new tab"
    >
      {/* Watchlist Button - Only show if user has permission */}
      {showWatchlistButton && onToggleWatchlist && (
        <button 
          className={styles.watchlistButton}
          onClick={onToggleWatchlist}
          title={isInWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          {isInWatchlist ? <FaBookmark /> : <FaRegBookmark />}
        </button>
      )}

      {/* Header */}
      <div className={styles.header}>
        {profilePhoto ? (
          <img 
            src={profilePhoto} 
            alt={profileName}
            className={styles.photo}
          />
        ) : (
          <div className={styles.photoPlaceholder}>
            {profileName?.charAt(0).toUpperCase()}
          </div>
        )}
        
        <div className={styles.headerInfo}>
          <h3 className={styles.name}>{profileName}</h3>
          {profile.headline && (
            <p className={styles.headline}>{profile.headline}</p>
          )}
          
          <div className={styles.meta}>
            {location && (
              <span className={styles.location}>
                <FaMapMarkerAlt /> {location}
              </span>
            )}
            {age && (
              <span className={styles.age}>Age {age}</span>
            )}
          </div>
        </div>
      </div>

      {/* Sports & Positions */}
      <div className={styles.sports}>
        <div className={styles.sportItem}>
          {getSportIcon()}
          <span>{profile.athleticProfile?.primarySport || 'Not specified'}</span>
        </div>
        
        {profile.athleticProfile?.positions?.length > 0 && (
          <div className={styles.positions}>
            {profile.athleticProfile.positions.slice(0, 2).map((position, index) => {
              // Handle both string and object formats
              const positionText = typeof position === 'string' 
                ? position 
                : position.position || '';
              
              return (
                <span key={index} className={styles.positionTag}>
                  {positionText}
                </span>
              );
            })}
            {profile.athleticProfile.positions.length > 2 && (
              <span className={styles.morePositions}>
                +{profile.athleticProfile.positions.length - 2} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className={styles.stats}>
        {/* Awards Count */}
        {profile.awardsCount > 0 && (
          <div className={styles.statItem}>
            <FaTrophy className={styles.statIcon} />
            <span className={styles.statValue}>{profile.awardsCount}</span>
            <span className={styles.statLabel}>Awards</span>
          </div>
        )}
        
        {/* Average Rating */}
        {averageRating > 0 && (
          <div className={styles.statItem}>
            <FaStar className={styles.statIcon} />
            <span className={styles.statValue}>{averageRating.toFixed(1)}</span>
            <span className={styles.statLabel}>Rating</span>
          </div>
        )}
        
        {/* Years Experience */}
        {profile.yearsExperience && (
          <div className={styles.statItem}>
            <span className={styles.statValue}>{profile.yearsExperience}</span>
            <span className={styles.statLabel}>Years Exp.</span>
          </div>
        )}
      </div>

      {/* Skills Preview */}
      {profile.topSkills && profile.topSkills.length > 0 && (
        <div className={styles.skills}>
          <h4 className={styles.skillsTitle}>Top Skills</h4>
          <div className={styles.skillTags}>
            {profile.topSkills.slice(0, 3).map((skill, index) => (
              <span key={index} className={styles.skillTag}>
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Availability Status */}
      {profile.availability?.status && profile.availability.status !== 'not_looking' && profile.availability.status !== 'committed' && (
        <div className={styles.availability}>
          <div className={`${styles.availabilityBadge} ${styles[profile.availability.status]}`}>
            {profile.availability.status === 'actively_looking' && 'Actively Looking'}
            {profile.availability.status === 'open_to_offers' && 'Open to Offers'}
            {profile.availability.status === 'open_to_opportunities' && 'Open to Opportunities'}
          </div>
        </div>
      )}

      {/* Match Score (if available from search) */}
      {profile.matchScore && (
        <div className={styles.matchScore}>
          <div className={styles.matchBar}>
            <div 
              className={styles.matchFill}
              style={{ width: `${profile.matchScore}%` }}
            />
          </div>
          <span className={styles.matchText}>
            {profile.matchScore}% Match
          </span>
        </div>
      )}
    </div>
  );
};

export default PlayerCard;
